import React, { useState, useEffect, useMemo, useCallback,useRef } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from 'react-hot-toast';
import { handlePrint } from "../api/printUtils.js";
import "../Styles/Invoice.css";
import "../Styles/EditInvoice.css";
import PrintToOfficeModal from "../components/PrintToOfficeModal"; // <-- Import the new component

const API_URL = process.env.REACT_APP_API_URL;

/* ---------- helpers ---------- */
const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: '2-digit', month: 'short' });
};

const formatFinYearForDisplay = (finyear) => {
    if (typeof finyear === 'string' && finyear.includes('-')) {
        const parts = finyear.split('-');
        if (parts.length === 2) {
            return `${parts[0].slice(-2)}-${parts[1].slice(-2)}`;
        }
    }
    return finyear;
};

const PriceSuggestion = ({ suggestions, onSelect }) => {
    if (!suggestions || suggestions.length === 0) {
        return null;
    }
    return (
        <div className="rate-suggestions-dropdown">
            {suggestions.map((suggestion, sIdx) => (
                <div 
                    key={sIdx} 
                    className="suggestion-item"
                    onMouseDown={() => onSelect(suggestion.data.rate)}
                >
                    {suggestion.type === 'customer_specific' ? (
                        <>
                            Last: <strong>₹{Number(suggestion.data.rate).toFixed(2)}</strong>
                            <span>(Inv #{suggestion.data.docno}/{formatFinYearForDisplay(suggestion.data.finyear)})</span>
                        </>
                    ) : (
                        <>
                            FYI: <strong>₹{Number(suggestion.data.rate).toFixed(2)}</strong>
                            <span>(To {suggestion.data.customerName.split(' ')[0]} / Inv #{suggestion.data.docno}/{formatFinYearForDisplay(suggestion.data.finyear)})</span>
                        </>
                    )}
                </div>
            ))}
        </div>
    );
};

const selectStyles = {
  container: (base) => ({ ...base, width: "100%" }),
  control: (base) => ({
    ...base,
    minHeight: 42,
    height: 42,
    borderColor: "#ddd",
    boxShadow: "none",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  singleValue: (base) => ({
    ...base,
    lineHeight: "42px",
    maxWidth: "calc(100% - 28px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0, lineHeight: 1 }),
  indicatorsContainer: (base) => ({ ...base, height: 42 }),
  dropdownIndicator: (base) => ({ ...base, padding: "0 6px" }),
  clearIndicator: (base) => ({ ...base, padding: "0 6px" }),
  menu: (base) => ({ ...base, zIndex: 20 }),
  option: (base) => ({
    ...base,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
};

const money = (n) =>
  (Number.isFinite(+n) ? +n : 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function buildTaxSummary(lineItems, discountPercent, taxApplicable) {
  const n = (v) => Number.parseFloat(v || 0) || 0;

  const basicTotal = lineItems.reduce((s, it) => s + n(it.qty) * n(it.rate), 0);
  const discountAmt = basicTotal * (n(discountPercent) / 100);

  const byRate = new Map(); // rate -> { rate, taxable, tax }
  lineItems.forEach((it) => {
    const basic = n(it.qty) * n(it.rate);
    const share = basicTotal > 0 ? (basic / basicTotal) * discountAmt : 0;
    const net = basic - share;
    const rate = taxApplicable ? n(it.taxper) : 0;
    const tax = net * (rate / 100);
    const key = rate.toFixed(2);
    const row = byRate.get(key) || { rate, taxable: 0, tax: 0 };
    row.taxable += net;
    row.tax += tax;
    byRate.set(key, row);
  });

  const rows = Array.from(byRate.values())
    .sort((a, b) => a.rate - b.rate)
    .map((r) => ({
      rate: r.rate,
      sgst: r.tax / 2,
      cgst: r.tax / 2,
      igst: 0,
      tax: r.tax,
      value: r.taxable,
    }));

  if (!rows.length)
    rows.push({ rate: "", sgst: 0, cgst: 0, igst: 0, tax: 0, value: 0 });

  return rows;
}

/* ---------- component ---------- */
const EditInvoice = () => {
  const { docno, finyear } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const doctype = (queryParams.get("doctype") || "INVOICE").toUpperCase();

  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const handleRateFocus = (index) => {
      const updated = [...lineItems];
      updated[index].showSuggestions = true;
      setLineItems(updated);
  };

const handleRateBlur = (index) => {
    setTimeout(() => {
        // This is the key change. We pass a function to setLineItems.
        // `currentItems` is guaranteed to be the most up-to-date state.
        setLineItems(currentItems => {
            const updated = [...currentItems];
            if (updated[index]) {
                updated[index].showSuggestions = false;
            }
            return updated; // Return the new, correct state
        });
    }, 200);
};

  // state
  const [customers, setCustomers] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [poDate, setPoDate] = useState("");
  const [customerCode, setCustomerCode] = useState("");
  const [remarks, setRemarks] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [selectedDiscountCode, setSelectedDiscountCode] = useState("");
  const [taxApplicable, setTaxApplicable] = useState(true);
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false); // <-- State to control modal
  const pdfWindowRef = useRef(null);
  const hasPresets = Array.isArray(discounts) && discounts.length > 0;

  // fetch data
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [custRes, discRes, invRes] = await Promise.all([
          axios.get(`${API_URL}/customers`, { headers }),
          axios.get(`${API_URL}/discounts`, { headers }),
          axios.get(
            `${API_URL}/document/details?docno=${docno}&finyear=${finyear}&doctype=${doctype}`,
            { headers }
          ),
        ]);

        setCustomers(custRes.data);
        setDiscounts(discRes.data);

        const inv = invRes.data;
        setInvoiceDate(inv.docdate?.split("T")[0] || "");
        setPoNumber(inv.pono || "");
        setPoDate(inv.podate ? inv.podate.split("T")[0] : "");
        setCustomerCode(inv.customer?.customercode || "");
        setRemarks(inv.remarks || "");

        // ✅ Load saved discount % from backend and sync dropdown if it matches a preset
        const savedDiscPer = Number.parseFloat(inv.discount_percent || 0) || 0;
        setDiscountPercent(savedDiscPer);
        const matched = (discRes.data || []).find(
          (d) => Number.parseFloat(d.discper) === savedDiscPer
        );
        setSelectedDiscountCode(matched ? matched.disccode : "");

        setLineItems(
          (inv.items || []).map((it) => ({
            prodcode: it.prodcode,
            prodname: it.prodname,
            qty: Number.parseFloat(it.qty) || 0,
            rate: Number.parseFloat(it.rate) || 0,
            taxper: Number.parseFloat(it.taxper) || 0,
            originalTax: Number.parseFloat(it.taxper) || 0, // remember original
            uom: it.uom || "",
            hsncode: it.hsncode || "",
            totalvalue: Number.parseFloat(it.basic_value) || 0, // pre-tax total
            suggestions: [], // Add this
            showSuggestions: false // Add this
          }))
        );
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem("token");
          navigate("/login");
          return;
        }
        console.error("Failed to load invoice for edit", err);
        toast.error(err?.response?.data?.detail || "Error loading invoice.");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [docno, finyear, doctype, headers, navigate]);

  // when toggle Apply Tax, zero / restore all rates
  useEffect(() => {
    setLineItems((prev) =>
      prev.map((it) => ({
        ...it,
        taxper: taxApplicable ? (it.originalTax ?? it.taxper ?? 0) : 0,
      }))
    );
  }, [taxApplicable]);
  useEffect(() => {
    if (lineItems.length > 0 && lineItems[0].prodcode) {
         setLineItems(prevItems =>
            prevItems.map(item => ({
                ...item,
                suggestions: [],
                showSuggestions: false
            }))
        );
    }
}, [customerCode]);
  // product details
  const fetchProductDetails = useCallback(
    async (prodcode) => {
      try {
        const res = await axios.get(`${API_URL}/products/${prodcode}`, { headers });
        return res.data;
      } catch {
        return null;
      }
    },
    [headers]
  );

  const loadProductOptions = useCallback(
    async (inputValue, callback) => {
      if (!inputValue) return callback([]);
      try {
        const res = await axios.get(
          `${API_URL}/products/search?query=${encodeURIComponent(inputValue)}`,
          { headers }
        );
        const options = res.data.items.map((p) => ({
          label: p.prodname,
          value: p.prodcode,
          ...p,
        }));
        callback(options);
      } catch {
        callback([]);
      }
    },
    [headers]
  );

  const handleLineItemChange = useCallback(
    async (index, field, value) => {
      if (field === 'prodcode' && value && !customerCode) {
        toast.error('Please select a customer before adding a product.');
        return;
      }
      
      const updated = [...lineItems];
      let item = { ...updated[index] };
      if (field === 'rate') {
          item.showSuggestions = false;
        }
      if (field === "prodcode") {
        item = { ...item, prodcode: "", prodname: "", qty: "", rate: "", taxper: 0, uom: "", hsncode: "", totalvalue: 0, originalTax: 0, suggestions: [], showSuggestions: false };
        if (value) {
            const details = value; // The full product object is passed from AsyncSelect
            const originalTax = Number(details.taxpercent || 0);
            item = { ...item, prodcode: details.prodcode, prodname: details.prodname, uom: details.uom, hsncode: details.hsncode, originalTax, taxper: taxApplicable ? originalTax : 0, rate: details.mrp || "" };
            if (customerCode && details.prodcode) {
                try {
                    const res = await axios.get(`${API_URL}/reports/product-history-for-customer?prodcode=${details.prodcode}&customercode=${customerCode}`, { headers });
                    if (res.data && res.data.type !== 'none' && res.data.data) {
                                            item.suggestions = [res.data];
                                        } else {
                                            item.suggestions = [];
                                        }                
                  } catch (error) { console.error("Failed to fetch price history:", error); }
            }
        }
      } else {
        if (field === 'rate') {
            item.showSuggestions = false;
        }
        item[field] = value;
      }

      item.totalvalue = (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0);
      updated[index] = item;
      setLineItems(updated);
    },
    [lineItems, taxApplicable, customerCode, headers]
);

  const handleAddLine = () => {
    setLineItems([
      ...lineItems,
      {
        prodcode: "",
        prodname: "",
        qty: "",
        rate: "",
        taxper: taxApplicable ? 0 : 0,
        originalTax: 0,
        uom: "",
        hsncode: "",
        totalvalue: 0,
        suggestions: [], 
        showSuggestions: false,
      },
    ]);
  };

  const handleRemoveLine = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  // totals
  const totals = useMemo(() => {
    const subtotal = lineItems.reduce(
      (sum, it) => sum + (parseFloat(it.totalvalue) || 0),
      0
    );
    const discountAmt = subtotal * ((parseFloat(discountPercent) || 0) / 100);
    const totalNet = subtotal - discountAmt;

    const totalTax = lineItems.reduce((sum, it) => {
      const basic = parseFloat(it.totalvalue) || 0;
      const share = subtotal > 0 ? (basic / subtotal) * discountAmt : 0;
      const net = basic - share;
      return sum + net * ((parseFloat(it.taxper) || 0) / 100);
    }, 0);

    return {
      subtotal,
      discountAmt,
      totalTax,
      grandTotal: totalNet + totalTax,
    };
  }, [lineItems, discountPercent]);

  const taxRows = useMemo(
    () => buildTaxSummary(lineItems, discountPercent, taxApplicable),
    [lineItems, discountPercent, taxApplicable]
  );

  // --- discount select/input sync like in create flow ---
  const handleDiscountSelect = (e) => {
    const code = e.target.value;
    setSelectedDiscountCode(code);
    const selected = (discounts || []).find((d) => d.disccode === code);
    setDiscountPercent(selected ? parseFloat(selected.discper) : 0);
  };

  const handleDiscountInputChange = (e) => {
    const value = e.target.value;
    const percent = value === "" ? 0 : parseFloat(value);
    const safe = isNaN(percent) ? 0 : percent;
    setDiscountPercent(safe);
    const matchingPreset = (discounts || []).find(
      (d) => parseFloat(d.discper) === safe
    );
    setSelectedDiscountCode(matchingPreset ? matchingPreset.disccode : "");
  };

  // submit
  const handleSubmit = async () => {
    if (!customerCode) {
      toast.error("Please select a customer.");
      return;
    }
    if (lineItems.some((i) => !i.prodcode)) {
      toast.error("Please ensure all lines have a product selected.");
      return;
    }
    const hasInvalidItem = lineItems.some(item => 
      !item.qty || parseFloat(item.qty) <= 0 || !item.rate || parseFloat(item.rate) <= 0
    );
    if (hasInvalidItem) {
        toast.error("Please ensure all items have a quantity and rate greater than zero.");
        return;
    }
    setIsSubmitting(true);

    const payload = {
      docdate: invoiceDate,
      finyear,
      customercode: customerCode,
      pono: poNumber,
      podate: poDate || null,
      remarks,
      discount_percent: parseFloat(discountPercent) || 0, // ✅ send edited/saved percent
      items: lineItems.map((it) => ({
        prodcode: it.prodcode,
        qty: parseFloat(it.qty) || 0,
        rate: parseFloat(it.rate) || 0,
        uom: it.uom || "",
        taxper: parseFloat(it.taxper) || 0,
      })),
    };

    try {
      await axios.put(
        `${API_URL}/document/${docno}/${finyear}?doctype=${doctype}`,
        payload,
        { headers }
      );
      await handlePrint(docno, finyear, doctype);
      toast.success(`${doctype} updated successfully!`);
      navigate("/dashboard");
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      console.error("Failed to update document", err);
      toast.error(err?.response?.data?.detail || "Update failed.");
    } finally{
      setIsSubmitting(false); 
    }
  };
const handleSubmitAndPrintAtOffice = async () => {
  if (!customerCode) {
    toast.error("Please select a customer.");
    return;
  }
  if (lineItems.some((i) => !i.prodcode)) {
    toast.error("Please ensure all lines have a product selected.");
    return;
  }
  const hasInvalidItem = lineItems.some(item => 
    !item.qty || parseFloat(item.qty) <= 0 || !item.rate || parseFloat(item.rate) <= 0
  );
  if (hasInvalidItem) {
    toast.error("Please ensure all items have a quantity and rate greater than zero.");
    return;
  }

  setIsSubmitting(true);
  try {
    // build the same payload as handleSubmit
    const payload = {
      docdate: invoiceDate,
      finyear,
      customercode: customerCode,
      pono: poNumber,
      podate: poDate || null,
      remarks,
      discount_percent: parseFloat(discountPercent) || 0,
      items: lineItems.map((it) => ({
        prodcode: it.prodcode,
        qty: parseFloat(it.qty) || 0,
        rate: parseFloat(it.rate) || 0,
        uom: it.uom || "",
        taxper: parseFloat(it.taxper) || 0,
      })),
    };

    // 1. Update invoice first
    await axios.put(
      `${API_URL}/document/${docno}/${finyear}?doctype=${doctype}`,
      payload,
      { headers }
    );

    // 2. Ask for number of copies
    setIsPrintModalOpen(true);

  } catch (err) {
    console.error("Error printing at office:", err);
    toast.error(err?.response?.data?.detail || "Failed to process request.");
  } finally {
    setIsSubmitting(false);
  }
};





  if (loading) return <p style={{ padding: 20 }}>Loading editor…</p>;

  return (
    <>


      <div className="invoice-form-container">
        {/* Header grid */}
        <div className="invoice-header">
          <div className="form-group doc-type">
            <label>Document Type</label>
            <input value={doctype} readOnly disabled />
          </div>

          <div className="form-group doc-number">
            <label>Document No.</label>
            <input value={docno} readOnly disabled />
          </div>

          <div className="form-group customer-select">
            <label>Customer</label>
            <select
              value={customerCode}
              onChange={(e) => setCustomerCode(e.target.value)}
            >
              <option value="">Select a customer</option>
              {customers.map((c) => (
                <option key={c.customercode} value={c.customercode}>
                  {c.customername}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>PO No.</label>
            <input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
          </div>

          <div className="form-group">
            <label>PO Date</label>
            <input
              type="date"
              value={poDate}
              onChange={(e) => setPoDate(e.target.value)}
            />
          </div>

          {/* Apply Tax (aligned nicely) */}
          <div className="form-group">
            <label>&nbsp;</label>
            <div className="apply-tax">
              <input
                id="applyTaxInv"
                type="checkbox"
                checked={taxApplicable}
                onChange={(e) => setTaxApplicable(e.target.checked)}
              />
              <label htmlFor="applyTaxInv">Apply Tax</label>
            </div>
          </div>
        </div>

        {/* Items table */}
        <div className="items-table-container">
          <table className="items-table">
            <thead>
              <tr>
                <th className="th-sno">S.No.</th> {/* ADDED */}
                <th className="th-product">Product</th>
                <th className="th-qty">Qty</th>
                <th className="th-rate">Rate</th>
                <th className="th-tax">Tax %</th>
                <th className="th-uom">UOM</th>
                <th className="th-hsn">HSN</th>
                <th className="th-total">Total</th>
                <th className="th-action"></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, idx) => (
                <tr key={idx}>
                  <td data-label="S.No." >{idx + 1}</td> {/* ADDED */}
                  <td data-label="Product">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadProductOptions}
                      value={
                        item.prodcode
                          ? { value: item.prodcode, label: item.prodname }
                          : null
                      }
                      onChange={(opt) => handleLineItemChange(idx, "prodcode", opt)}
                      placeholder="Type to search…"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>
                  <td data-label="Qty">
                    <input
                      type="number"
                      value={item.qty}
                      onChange={(e) => handleLineItemChange(idx, "qty", e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      required
                    />
                  </td>
                  <td data-label="Rate" className="rate-cell">
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleLineItemChange(idx, "rate", e.target.value)}
                        onFocus={() => handleRateFocus(idx)}
                        onBlur={() => handleRateBlur(idx)}
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                      {item.showSuggestions && (
                          <PriceSuggestion 
                              suggestions={item.suggestions}
                              onSelect={(selectedRate) => handleLineItemChange(idx, 'rate', selectedRate)}
                          />
                      )}
                  </td>

                  <td data-label="Tax %">
                    <input
                      type="number"
                      value={item.taxper}
                      readOnly
                      disabled={!taxApplicable}
                    />
                  </td>
                  <td data-label="UOM">
                    <input value={item.uom || ""} readOnly disabled />
                  </td>
                  <td data-label="HSN">
                    <input value={item.hsncode || ""} readOnly disabled />
                  </td>
                  <td data-label="Total">
                    <input
                      value={(item.totalvalue || 0).toFixed(2)}
                      readOnly
                      disabled
                    />
                  </td>
                  <td>
                    <button className="remove-btn" onClick={() => handleRemoveLine(idx)}>
                      &times;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="add-line-btn" onClick={handleAddLine}>
            + Add Item
          </button>
        </div>

        {/* Tax table + remarks + totals */}
        <div className="invoice-footer footer-with-tax">
          <div className="left-block">
            <table className="tax-summary">
              <thead>
                <tr>
                  <th>Tax%</th>
                  <th>SGST</th>
                  <th>CGST</th>
                  <th>IGST</th>
                  <th>TaxAmt</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
              {taxRows.map((r, i) => (
                <tr key={i}>
                  <td data-label="Tax%" className="ta-center">{r.rate === "" ? "" : Number(r.rate).toString()}</td>
                  <td data-label="SGST" className="ta-right">{money(r.sgst)}</td>
                  <td data-label="CGST" className="ta-right">{money(r.cgst)}</td>
                  <td data-label="IGST" className="ta-right">{money(r.igst)}</td>
                  <td data-label="TaxAmt" className="ta-right">{money(r.tax)}</td>
                  <td data-label="Value" className="ta-right">{money(r.value)}</td>
                </tr>
              ))}
            </tbody>
            </table>

            <div className="remarks-inline">
              <label htmlFor="remarksInv">Remarks</label>
              <textarea
                id="remarksInv"
                rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
          </div>

          {/* --- Totals (with discount preset + manual %) --- */}
          <div className="totals-section">
            <div className="totals-row">
              <span>Subtotal</span>
              <span>₹{totals.subtotal.toFixed(2)}</span>
            </div>

            <div className={`totals-row discount-row ${hasPresets ? "has-preset" : ""}`}>
              <label htmlFor="discountPreset" className="discount-text">Discount</label>

              {hasPresets && (
                <select
                  id="discountPreset"
                  className="discount-select"
                  value={selectedDiscountCode}
                  onChange={handleDiscountSelect}
                >
                  <option value="">(Select Preset)</option>
                  {discounts.map((d) => (
                    <option key={d.disccode} value={d.disccode}>
                      {d.discname} ({d.discper}%)
                    </option>
                  ))}
                </select>
              )}

              <div className="discount-input-group">
                <input
                  type="number"
                  className="discount-input"
                  value={discountPercent}
                  onChange={handleDiscountInputChange}
                  onWheel={(e) => e.currentTarget.blur()}
                  step="0.01"
                  placeholder="0"
                  aria-label="Discount percent"
                />
                <span>%</span>
              </div>

              <span className="discount-amount">- ₹{totals.discountAmt.toFixed(2)}</span>
            </div>

            <div className="totals-row">
              <span>Total Tax</span>
              <span>₹{totals.totalTax.toFixed(2)}</span>
            </div>

            <div className="totals-row grand-total">
              <span>Grand Total</span>
              <span>₹{Math.round(totals.grandTotal).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* actions */}
        <div className="form-actions edit-actions">
          <button className="btn ghost" type="button" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button
            className="submit-btn"
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            title={`Update & Print ${doctype}`}
          >
            {isSubmitting ? "Updating..." : `Update & Print ${doctype}`}
          </button>
          <button
            className="submit-btn-office"
            type="button"
            onClick={handleSubmitAndPrintAtOffice}
            disabled={isSubmitting}
            title={`Update & Print ${doctype} at Office`}
          >
            {isSubmitting ? "Submitting..." : `Update & Print at Office`}
          </button>

        </div>
          {isPrintModalOpen && (
            <PrintToOfficeModal
              isOpen={isPrintModalOpen}
              docno={docno}
              finyear={finyear}
              doctype={doctype}
              headers={headers}
              onClose={() => {
              setIsPrintModalOpen(false);
              toast.success(`${doctype} was updated successfully!`);
              navigate("/dashboard");
            }}
            />
          )}
        
      </div>
    </>
  );
};

export default EditInvoice;
